import type { AwardLetters, LetterWordingSource } from '@myrewards/contracts';
import { useState, type ReactElement } from 'react';
import { saveLetterWording } from '../../api/letters';
import { problemText } from '../../settings/parts';
import { Button, Card, ConfirmDialog, TextField, plural } from '../../ui';
import s from '../Steps.module.css';
import o from '../Ops.module.css';
import { wordingHint } from './presenter';

const FROM: Readonly<Record<LetterWordingSource, string>> = {
  entered: 'As entered with the award, or as the Stock Admin team changed it.',
  program: 'From the program’s name: nobody has typed wording of their own.',
  title: 'From the award name: nobody has typed wording of their own.',
  none: 'None yet: the award has no program, no name and no wording of its own.',
};

/**
 * APP-201 D5: the award's letter wording, once for everyone on it. The person who entered the award
 * set it, or it follows the award name, else the program's name; the Stock Admin team can change
 * it here before the letters go out. Saving replaces any person's own wording from before, and the
 * letters already made are made again to take it.
 */
export function LetterWordingCard({
  letters,
  version,
  canEdit,
  onSaved,
}: {
  letters: AwardLetters;
  version: number;
  canEdit: boolean;
  onSaved: (next: AwardLetters, said: string) => Promise<void>;
}): ReactElement | null {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [confirming, setConfirming] = useState<'save' | 'default' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const wording = letters.wording;
  if (wording == null) return null;
  const own = wording.ownWording;
  const notes = letters.lines.find((l) => l.selectedTemplate != null)?.selectedTemplate?.notes;
  const hint = wordingHint(letters.type, notes);
  const next = confirming === 'default' ? null : draft.trim();
  return (
    <Card
      title="Letter wording"
      help={{
        shows:
          'The wording every person’s letter on this award prints, and where it comes from: entered with the award, the award name or the program’s name.',
        canDo:
          'Change it once for everyone whose letter hasn’t gone out, with a reason. Generate the letters again to take the new wording.',
      }}
    >
      <div className={o.stack}>
        <p className={o.strong}>{wording.text ?? 'No wording yet'}</p>
        <p className={o.muted}>{FROM[wording.source]}</p>
        {own > 0 && (
          <p className={o.muted}>
            {plural(own, 'person’s letter keeps', 'people’s letters keep')} their own wording from
            before. A change here replaces it, so everyone reads the same.
          </p>
        )}
        {canEdit && letters.actions.wording === true && editing && (
          <>
            <TextField
              label="Letter wording"
              multiline
              value={draft}
              onChange={setDraft}
              maxLength={300}
              isRequired
              description={`Printed in every person’s letter on this award. ${hint} Use only wording approved for the recipients; confidential justification stays separate.`}
            />
            <div className={s.generateRow}>
              <Button
                variant="ghost"
                isDisabled={draft.trim() === '' || (draft.trim() === wording.text && own === 0)}
                onPress={() => {
                  setError(null);
                  setConfirming('save');
                }}
              >
                Review wording
              </Button>
              <Button
                variant="ghost"
                onPress={() => {
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </>
        )}
        {canEdit && letters.actions.wording === true && !editing && (
          <div className={s.generateRow}>
            <Button
              variant="ghost"
              size="sm"
              onPress={() => {
                setDraft(wording.text ?? '');
                setEditing(true);
                setError(null);
              }}
            >
              Change the letter wording
            </Button>
            {wording.source === 'entered' && (
              <Button
                variant="ghost"
                size="sm"
                onPress={() => {
                  setError(null);
                  setConfirming('default');
                }}
              >
                Use the award or program name
              </Button>
            )}
          </div>
        )}
      </div>
      <ConfirmDialog
        isOpen={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
        title="Save the letter wording?"
        intro={`Every letter on this award still to go out will print it${own > 0 ? `, including ${plural(own, 'person', 'people')} with their own wording from before` : ''}. Generate the letters again after saving, then read the new ones.`}
        summary={[
          {
            label: 'Letter wording',
            value: next ?? 'The award name, else the program’s name',
          },
        ]}
        confirmLabel="Save wording"
        error={error}
        onConfirm={async (reason) => {
          try {
            const saved = await saveLetterWording(letters.requestId, {
              version,
              letterWording: next,
              reason,
            });
            setEditing(false);
            setConfirming(null);
            await onSaved(saved, 'Letter wording saved');
            return true;
          } catch (err) {
            setError(problemText(err));
            return false;
          }
        }}
      />
    </Card>
  );
}
